import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axios from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import {
  capitalizeFirstLetter,
  convertEpochToDateString,
  formatDateTimeToDate
} from '../../../../../utils/commonFunctions';

type ReturnedType = any;
type ThunkArg = {
  description: string;
  dimensionUnit: string;
  dotInspectionDate: number;
  insuranceModel: {
    documentNames: any;
    expireDate: number;
    registerNumber: number;
  };
  insuranceName: string;
  length: number;
  licenseModel: {
    documentNames: any;
    expireDate: number;
    registerNumber: number;
  };
  make: string;
  model: string;
  modelYear: number;
  ownedOrHired: string;
  ownerName: string;
  registeredState: string;
  serialNumber: string;
  trailerAxleNumber: number;
  trailerGroup: string;
  trailerName: string;
  trailerType: string;
  trailerUnladenWeight: number;
  trailerUnladenWeightUnit: string;
  unitNumber: string;
  vinNumber: number;
  width: number;
  yearPurchased: number;
  titleNumber: string;
};

interface Trailer {
  description: string;
  dimensionUnit: string;
  insuranceModel: {
    documentNames: any;
    expireDate: number;
    number: string[];
    registerNumber: number;
    registeredDate: number;
  };
  length: number;
  licenseModel: {
    documentNames: any;
    expireDate: number;
    number: string[];
    registerNumber: number;
    registeredDate: number;
  };
  make: string;
  model: string;
  modelYear: number;
  ownedOrHired: string;
  ownerName: string;
  registeredState: string;
  serialNumber: string;
  trailerAxleNumber: number;
  trailerGroup: string;
  trailerName: string;
  trailerType: string;
  trailerUnladenWeight: number;
  trailerUnladenWeightUnit: string;
  unitNumber: string;
  vinNumber: number;
  width: number;
  yearPurchased: number;
  titleNumber: string;
}

type initialStateProps = {
  isLoading: boolean;
  type: string;
  data: any;
  error: string | null;
  count: null;
  fileUrls: string[];
};

const initialState: initialStateProps = {
  isLoading: false,
  type: 'addTrailer',
  data: [],
  error: null,
  count: null,
  fileUrls: []
};

const pagination = {
  pageNo: 1,
  pageSize: 5
};

// add trailer
export const addTrailer = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/addTrailer',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_TRAILER}`,
        data
      });
      await thunkApi.dispatch(getTrailer(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get trailer
export const getTrailer = createAsyncThunk<ReturnedType, any>(
  'api/getTrailer',
  async (data, thunkApi) => {
    try {
      const {
        pageNo = 1,
        pageSize = 5,
        sortColumnName,
        sortBy,
        searchInput,
        searchColumnName,
        signal
      } = data;

      let url = `${URLs.GET_ALL_TRAILER}?pageNo=${pageNo}&pageSize=${pageSize}`;

      if (sortBy && sortColumnName && searchColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput && searchColumnName) {
        url += `&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput && searchColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput) {
        url += `&searchInput=${searchInput}`;
      } else if (sortBy && sortColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error?.response?.data?.message
              : error?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

// delete trailer
export const deleteTrailer = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/deleteTrailer',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DEL_TRAILER}${data}`
      });
      await thunkApi.dispatch(getTrailer(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//deactivate trailer
export const deactiveTrailer = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/deactivate',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.DEACTIVATE_TRAILER}${data}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update trailer
export const updateTrailer = createAsyncThunk<ReturnedType, any>(
  'update/trailer',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_TRAILER}${data.id}`,
        data: data.updatePayload
      });
      await thunkApi.dispatch(getTrailer(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// add trailer slice
const addTrailerSlice = createSlice({
  name: 'addTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addTrailer.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addTrailer.fulfilled, (state, action: any) => {
      state.data = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addTrailer.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

// get trailer slice
const getTrailerSlice = createSlice({
  name: 'getTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTrailer.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.type = 'getTrailer';
      })
      .addCase(getTrailer.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        const {
          data: { trailerList, count }
        } = action.payload;
        state.data = trailerList?.map((item: any) => ({
          id: item?.id,
          ownerName: item?.ownerName || 'NA',
          ownerNameCaps: capitalizeFirstLetter(item?.ownerName) || 'NA',
          vinNumber: item?.vinNumber,
          vinNumberCaps: capitalizeFirstLetter(item?.vinNumber),
          serial_number: item?.serial_number,
          serial_numberCaps: capitalizeFirstLetter(item?.serial_number),
          unitNumber: item?.unitNumber,
          unitNumberCaps: (item?.unitNumber).toUpperCase(),
          modelYear: item?.modelYear,
          trailerUnladenWeight: item?.trailerUnladenWeight,
          trailerUnladenWeightUnit: item?.trailerUnladenWeightUnit,
          trailerAxleNumber: item?.trailerAxleNumber,
          description: item?.description || 'NA',
          descriptionCaps: capitalizeFirstLetter(item?.description) || 'NA',
          length: item?.length,
          width: item?.width,
          dimensionUnit: item?.dimensionUnit,
          isActive: item?.isActive,
          make: item?.make,
          makeCaps: capitalizeFirstLetter(item?.make),
          model: item?.model,
          modelCaps: capitalizeFirstLetter(item?.model),
          ownedOrHired: item?.ownedOrHired,
          trailerType: item?.trailerType,
          trailerGroup: item?.trailerGroup,
          yearPurchased: item?.yearPurchased,
          registeredState: item?.registeredState,
          registeredStateCaps: capitalizeFirstLetter(item?.registeredState),
          insuranceDocumentNames: item?.insuranceModel?.documentUploadInfoList?.map(
            (item: any) => item.fileName
          ),
          insuranceRegisterNumber: item?.insuranceModel?.registerNumber,
          insuranceRegisterNumberCaps: capitalizeFirstLetter(
            item?.insuranceModel?.registerNumber
          ),
          insuranceExpireDate: item?.insuranceModel?.expireDate,
          insuranceExpiry: formatDateTimeToDate(
            convertEpochToDateString(item?.insuranceModel?.expireDate)
          ),
          insuranceGetURLList: item?.insuranceModel?.getURLList,
          licenseDocumentNames: item?.licenseModel?.documentUploadInfoList?.map(
            (item: any) => item.fileName
          ),
          licenseRegisterNumber: item?.licenseModel?.registerNumber,
          licenseRegisterNumberCaps: capitalizeFirstLetter(
            item?.licenseModel?.registerNumber
          ),
          licenseExpireDate: item?.licenseModel?.expireDate,
          licenseExpiry: formatDateTimeToDate(
            convertEpochToDateString(item?.licenseModel?.expireDate)
          ),
          licenseGetURLList: item?.licenseModel?.getURLList,
          dotInspectionDateFormat: item?.dotInspectionDate,
          dotInspectionDate: formatDateTimeToDate(
            convertEpochToDateString(item?.dotInspectionDate)
          ),
          insuranceName: item?.insuranceName,
          type: 'Trailer'
        }));
        state.count = count;
        state.error = null;
      })
      .addCase(getTrailer.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// deactivate trailer slice
const deactivateTrailerSlice = createSlice({
  name: 'deactivateTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deactiveTrailer.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      deactiveTrailer.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.error = null;
      }
    );
    builder.addCase(deactiveTrailer.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

// update trailer slice
const updateTrailerSlice = createSlice({
  name: 'updateTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateTrailer.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(
      updateTrailer.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      }
    );
    builder.addCase(updateTrailer.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload.response.data.error || 'Something went wrong';
    });
  }
});

// delete trailer slice
const deleteTrailerSlice = createSlice({
  name: 'deleteTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(deleteTrailer.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      deleteTrailer.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = state.data.filter((row: any) => row.id !== action.payload.id);
        state.error = null;
      }
    );
    builder.addCase(deleteTrailer.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

//action for upload file
export const fileUploadTrailer = createAsyncThunk<any, any>(
  'fileuploadtrailer',
  async (data, thunkApi) => {
    const { url, file, id, setFiles, setUploadedFilesCount, totalFiles } = data;
    try {
      const response = await axios({
        method: 'PUT',
        url: url,
        data: file
      });
      if (response) {
        setFiles((prev: any) => {
          if (url.includes('License')) {
            return {
              ...prev,
              license: [...prev.license, file.name]
            };
          } else if (url.includes('Insurance')) {
            return {
              ...prev,
              insurance: [...prev.insurance, file.name]
            };
          }
        });

        setUploadedFilesCount((prevCount: number) => prevCount + 1);

        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Trailer file uploaded successfully',
            severity: 'success'
          })
        );
      }
      return response;
    } catch (error) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'File Upload Failed',
          severity: 'error'
        })
      );
      throw error;
    }
  }
);

// slice for upload file
const fileUploadTrailerSlice = createSlice({
  name: 'fileUploadTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fileUploadTrailer.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fileUploadTrailer.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.fileUrls.push(action.payload?.fileUrl || '');
        state.error = null;
      })
      .addCase(fileUploadTrailer.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.message || 'Upload failed';
      });
  }
});

// add status code post API for file upload
export const addFileStatus = createAsyncThunk<ReturnedType, any>(
  'addFileStatus',
  async (data, thunkApi) => {
    try {
      const { unitNumber, ...payload } = data;
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.FILE_STATUS}trailerUnitNumber=${unitNumber}`,
        data: payload
      });
      await thunkApi.dispatch(getTrailer(pagination));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// slice for add status code post API for file upload
const addFileStatusSlice = createSlice({
  name: 'addFileStatus',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(addFileStatus.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(addFileStatus.fulfilled, (state, action: any) => {
      state.data = action?.payload?.data;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addFileStatus.rejected, (state, action: any) => {
      state.error = action.error.message || 'Something went wrong';
      state.isLoading = false;
    });
  }
});

export const addTrailerReducer = addTrailerSlice.reducer;
export const addFileStatusReducer = addFileStatusSlice.reducer;
export const getTrailerReducer = getTrailerSlice.reducer;
export const deleteTrailerReducer = deleteTrailerSlice.reducer;
export const deactivateTrailerReducer = deactivateTrailerSlice.reducer;
export const updateTrailerReducer = updateTrailerSlice.reducer;
export const fileUploadTrailerReducer = fileUploadTrailerSlice.reducer;
